import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import {
  Calendar,
  ChevronRight,
  BookOpen,
  AlertCircle,
  Mail,
  Activity,
  ChevronDown,
  Bell,
  Paperclip,
  Download,
  ExternalLink,
  FileQuestion,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HomeShell,
  HomeHeader,
  HomeAlertStrip,
  HomePrimaryGrid,
  HomeFabDock,
} from '../../components/home'
import { TonightsHomework, PrepareSection, AttentionSection, RecentlyGradedSection, ViewSelector, ThisWeekView, FloatingActionButton } from '../../components/parent'
import type { DashboardView } from '../../components/parent'
import ChildActivityFeed from '../../components/parent/ChildActivityFeed'
import type {
  DashboardHomeworkResponse,
  StudentHomework,
  AnnouncementItem,
  AttachmentItem,
} from '../../lib/api-types'

// Alias for backwards compatibility within this file
type DashboardResponse = DashboardHomeworkResponse

// Re-export types used by child components
export type { StudentHomework, AnnouncementItem }

interface UnlinkedScan {
  id: number
  file_name: string
  detected_title: string | null
  detected_score: number | null
  scan_date: string | null
  source: string
}

export default function ParentDashboard() {
  const navigate = useNavigate()
  const [activeView, setActiveView] = useState<DashboardView>('today')

  const { data, isLoading, error } = useQuery<DashboardResponse>({
    queryKey: ['parent-dashboard-homework'],
    queryFn: async () => {
      const response = await api.get('/api/parent/dashboard-homework')
      return response.data
    },
  })

  // Fetch unread message count
  const { data: messagesData } = useQuery<{ unread_count: number }>({
    queryKey: ['parent-messages-count'],
    queryFn: async () => {
      const response = await api.get('/api/parent/messages?scope=unread')
      return response.data
    },
    refetchInterval: 60000,
  })

  // Fetch unlinked scans count
  const { data: unlinkedData } = useQuery<{ scans: UnlinkedScan[]; count: number }>({
    queryKey: ['parent-unlinked-scans'],
    queryFn: async () => {
      const response = await api.get('/api/parent/unlinked-scans')
      return response.data
    },
  })

  const unreadCount = messagesData?.unread_count || 0
  const unlinkedScans = unlinkedData?.scans || []

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-surface-card rounded"></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-surface-card rounded-xl"></div>
          <div className="h-80 bg-surface-card rounded-xl"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-gray-400">Failed to load dashboard</p>
      </div>
    )
  }

  const students = data?.students || []
  const reportDate = data?.date || new Date().toISOString().split('T')[0]
  const totalMissing = students.reduce((sum, student) => sum + student.stats.missing_count, 0)
  const totalHomework = students.reduce((sum, student) => sum + student.stats.homework_total_tonight, 0)
  const totalHomeworkDone = students.reduce((sum, student) => sum + student.stats.homework_completed_tonight, 0)

  return (
    <HomeShell>
      <HomeHeader
        title={activeView === 'week' ? 'This Week' : "Tonight's Homework"}
        subtitle={(
          <>
            <Calendar className="w-3.5 h-3.5 inline mr-1" />
            {new Date(reportDate + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </>
        )}
        actions={(
          <div className="flex items-center gap-2">
            <Link
              to="/parent/messages"
              className="relative p-2 bg-surface-card hover:bg-surface-hover rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5 text-blue-400" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
          </div>
        )}
      />

      <div className="card border border-blue-500/30 bg-blue-500/5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-blue-300 font-medium">Scan Workspace</p>
            <p className="text-xs text-blue-200/80 mt-1">
              Use the bottom-right icons: + to scan, magnifier to review and manage scans.
            </p>
          </div>
          <Link
            to="/parent/scans"
            className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors"
          >
            Open Workspace
          </Link>
        </div>
      </div>

      <ViewSelector activeView={activeView} onChange={setActiveView} />

      {unlinkedScans.length > 0 && (
        <HomeAlertStrip>
          <div className="bg-orange-900/30 border border-orange-500/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileQuestion className="w-6 h-6 text-orange-400" />
                <div>
                  <p className="font-medium text-orange-300">
                    {unlinkedScans.length} scan{unlinkedScans.length > 1 ? 's' : ''} need{unlinkedScans.length === 1 ? 's' : ''} your help
                  </p>
                  <p className="text-sm text-orange-400/80">
                    We couldn't figure out which child these belong to
                  </p>
                </div>
              </div>
              <Link
                to="/parent/scans"
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Review
              </Link>
            </div>
          </div>
        </HomeAlertStrip>
      )}

      <HomePrimaryGrid>
        <div>
          {activeView === 'today' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {students.map((student) => (
                  <StudentCard key={student.id} student={student} />
                ))}
              </div>

              {students.length === 0 && (
                <div className="card text-center py-12">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No students found</p>
                </div>
              )}
            </>
          )}

          {activeView === 'week' && (
            students.length > 0 ? (
              <ThisWeekView students={students} />
            ) : (
              <div className="card text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">No students found</p>
              </div>
            )
          )}
        </div>

        <div className="space-y-6">
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Household Snapshot</h2>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-surface-hover rounded-lg p-3">
                <p className="text-xl font-bold">{students.length}</p>
                <p className="text-xs text-gray-400">Students</p>
              </div>
              <div className="bg-surface-hover rounded-lg p-3">
                <p className="text-xl font-bold">{totalHomeworkDone}/{totalHomework}</p>
                <p className="text-xs text-gray-400">Homework Done</p>
              </div>
              <div className="bg-surface-hover rounded-lg p-3">
                <p className="text-xl font-bold">{totalMissing}</p>
                <p className="text-xs text-gray-400">Missing</p>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold mb-3">Open Areas</h2>
            <div className="space-y-2">
              <Link to="/parent/courses" className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-sm text-gray-200 hover:bg-gray-700/70 transition-colors">
                <span>Courses</span>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </Link>
              <Link to="/parent/games" className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-sm text-gray-200 hover:bg-gray-700/70 transition-colors">
                <span>Arcade</span>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </Link>
              <Link to="/parent/scans" className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2 text-sm text-gray-200 hover:bg-gray-700/70 transition-colors">
                <span>Scan Workspace</span>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </Link>
            </div>
          </div>
        </div>
      </HomePrimaryGrid>

      <HomeFabDock>
        <FloatingActionButton
          onClick={() => navigate('/parent/scans')}
          onReview={() => navigate('/parent/scans')}
        />
      </HomeFabDock>
    </HomeShell>
  )
}

function StudentCard({ student }: { student: StudentHomework }) {
  const [showActivity, setShowActivity] = useState(false)

  return (
    <div className="card border-gray-700/50 hover:border-gray-600 transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">{student.name}</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Level</span>
          <div className="level-badge text-sm">{student.level}</div>
        </div>
      </div>

      {/* Section 0: Recently Graded */}
      <RecentlyGradedSection items={student.recently_graded || []} />

      {/* Section 1: Upcoming Tests (if any urgent ones) */}
      <PrepareSection items={student.prepare_for} />

      {/* Section 2: Tonight's Homework - Primary Focus */}
      <TonightsHomework
        studentId={student.id}
        items={student.tonights_homework}
        completed={student.stats.homework_completed_tonight}
        total={student.stats.homework_total_tonight}
      />

      {/* Section 3: Needs Attention */}
      <AttentionSection items={student.needs_attention} />

      {/* Section 4: Recent Announcements (compact, collapsed by default) */}
      <AnnouncementsSection items={student.announcements} studentId={student.id} />

      {/* Section 5: Recent Activity (collapsible) */}
      <div className="mt-4 pt-3 border-t border-gray-700/50">
        <button
          onClick={() => setShowActivity(!showActivity)}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-sm font-medium text-gray-400 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Recent Activity
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${
              showActivity ? 'rotate-180' : ''
            }`}
          />
        </button>
        {showActivity && (
          <div className="mt-3">
            <ChildActivityFeed studentId={student.id} limit={5} />
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-700/50">
        <div className="flex items-center gap-4 text-sm">
          {student.stats.average_grade !== null && (
            <span className={`font-medium ${
              student.stats.average_grade >= 90 ? 'text-green-400' :
              student.stats.average_grade >= 80 ? 'text-blue-400' :
              student.stats.average_grade >= 70 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              Avg: {student.stats.average_grade}%
            </span>
          )}
          {student.stats.missing_count > 0 ? (
            <span className="text-red-400 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {student.stats.missing_count} missing
            </span>
          ) : (
            <span className="text-green-400">0 missing</span>
          )}
        </div>

        <Link
          to={`/parent/courses/student/${student.id}`}
          className="flex items-center gap-1 text-sm text-quest-primary hover:text-quest-secondary transition-colors"
        >
          View Details
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  )
}

function AttachmentLink({ attachment }: { attachment: AttachmentItem }) {
  const [isLoading, setIsLoading] = useState(false)

  const handleDownload = async () => {
    if (attachment.spaces_path) {
      setIsLoading(true)
      try {
        const response = await api.get(`/api/parent/files/${attachment.spaces_path}`)
        const { url } = response.data
        window.open(url, '_blank')
      } catch (error) {
        console.error('Failed to get download URL:', error)
        if (attachment.url) {
          window.open(attachment.url, '_blank')
        }
      } finally {
        setIsLoading(false)
      }
    } else if (attachment.url) {
      window.open(attachment.url, '_blank')
    }
  }

  const formatSize = (bytes: number | null) => {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <button
      onClick={handleDownload}
      disabled={isLoading}
      className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs text-gray-300 flex items-center gap-1 transition-colors disabled:opacity-50"
    >
      <Download className="w-3 h-3" />
      <span className="truncate max-w-[150px]">{attachment.display_name}</span>
      {attachment.size && (
        <span className="text-gray-500 text-[10px]">({formatSize(attachment.size)})</span>
      )}
    </button>
  )
}

function AnnouncementsSection({ items, studentId }: { items: AnnouncementItem[], studentId: number }) {
  const queryClient = useQueryClient()
  const [sectionExpanded, setSectionExpanded] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

  const markAsReadMutation = useMutation({
    mutationFn: async (announcementId: number) => {
      await api.patch(`/api/parent/announcements/${announcementId}/read`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-dashboard-homework'] })
    },
  })

  const unreadItems = items.filter(a => !a.read)

  if (unreadItems.length === 0) {
    return null
  }

  const toggleItemExpanded = (id: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleDismiss = (e: React.MouseEvent, announcementId: number) => {
    e.stopPropagation()
    markAsReadMutation.mutate(announcementId)
  }

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="mt-4 pt-3 border-t border-gray-700/50">
      <button
        onClick={() => setSectionExpanded(!sectionExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-sm font-medium text-gray-400 flex items-center gap-2">
          <Bell className="w-4 h-4 text-yellow-400" />
          New Announcements
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
            {unreadItems.length}
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            sectionExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {sectionExpanded && (
        <div className="mt-3 space-y-1">
          {unreadItems.map((announcement) => {
            const isExpanded = expandedItems.has(announcement.id)
            return (
              <div
                key={announcement.id}
                className="rounded-lg bg-surface-card/50 border border-gray-700/50 overflow-hidden"
              >
                <button
                  onClick={() => toggleItemExpanded(announcement.id)}
                  className="w-full p-2 flex items-center gap-2 text-left hover:bg-surface-hover/50 transition-colors"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 flex-shrink-0" />
                  <span className="flex-1 min-w-0 text-sm font-medium text-white truncate">
                    {announcement.title}
                  </span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {announcement.course}
                  </span>
                  <button
                    onClick={(e) => handleDismiss(e, announcement.id)}
                    className="p-1 hover:bg-gray-600 rounded transition-colors flex-shrink-0"
                    title="Mark as read"
                  >
                    <X className="w-3.5 h-3.5 text-gray-400 hover:text-white" />
                  </button>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-gray-700/50">
                    <p className="text-xs text-gray-400 mb-2">
                      {announcement.author && `Posted by ${announcement.author}`}
                      {announcement.author && announcement.posted_at && ' \u2022 '}
                      {announcement.posted_at && formatRelativeTime(announcement.posted_at)}
                    </p>
                    {announcement.preview && (
                      <p className="text-sm text-gray-300 mb-2">
                        {announcement.preview}
                      </p>
                    )}

                    {announcement.attachments && announcement.attachments.length > 0 && (
                      <div className="mt-2 mb-2">
                        <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                          <Paperclip className="w-3 h-3" />
                          Attachments
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {announcement.attachments.map((att) => (
                            <AttachmentLink key={att.id} attachment={att} />
                          ))}
                        </div>
                      </div>
                    )}

                    {announcement.html_url && (
                      <a
                        href={announcement.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-quest-primary hover:text-quest-secondary inline-flex items-center gap-1"
                      >
                        View in Canvas
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          <Link
            to={`/parent/courses/student/${studentId}`}
            className="block text-center text-xs text-gray-400 hover:text-quest-primary pt-2 transition-colors"
          >
            View all announcements in course details
          </Link>
        </div>
      )}
    </div>
  )
}
